import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Shell from '../../components/Shell/Shell';
import { generateContent } from '../../service/ContentGeneration';
import Alert from '../../components/Alert/Alert';
import { mapError } from '../../utils/alerts/error';
import { handleError } from '../../service/base.service';
import { redirectUnauthorized } from '../../utils/errors';
import { getFormComponent } from './Forms';
import { CONTENT_TYPE } from '../../enums';

const Home = ({ initialState = CONTENT_TYPE.MSLP.value }) => {
  const [contentType, setContentType] = useState(initialState);
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const [initialValues, setInitialValues] = useState(null);

  const hasError = error !== null;
  const initialValueSource = searchParams.get('initialValues');

  useEffect(() => {
    if (initialValueSource !== 'clipboard') {
      setInitialValues(null);
      return;
    }

    const setInitialValuesWithClipboard = async () => {
      const dataRaw = await navigator.clipboard.readText();
      const data = JSON.parse(dataRaw);

      let { contentType: initialContentType } = data;
      if (
        !initialContentType ||
        !['blog', 'mslp', 'other', 'inventory'].includes(initialContentType)
      ) {
        initialContentType = 'mslp';
      }

      setContentType(initialContentType);
      setInitialValues({ ...data, contentType: initialContentType });
    };

    setInitialValuesWithClipboard().catch((e) => {
      setError(mapError('ClipboardFormInitializationError', e.message));
    });
  }, [initialValueSource]);

  const handleSubmit = async (formValues) => {
    try {
      const response = await generateContent(formValues);
      navigate(`/prompt/${response.contentRequestId}/${response.lastRequestTryNumber}`, {
        state: { input: { ...formValues, ...response } }
      });
    } catch (e) {
      handleError(e, {
        UnauthorizedError: () => redirectUnauthorized(navigate),
        default: () => setError(mapError(e.name, e.message))
      });
    }
  };

  const renderError = (error) => {
    return (
      <div className="mb-4">
        <Alert level="error" label={error.label}>
          {error.details}
        </Alert>
      </div>
    );
  };

  const renderContentTypeForm = (contentType) => {
    const FormComponent = getFormComponent(contentType);

    return (
      <FormComponent
        contentType={contentType}
        handleContentType={setContentType}
        onSubmit={handleSubmit}
        initialValues={initialValues}
      />
    );
  };

  const renderHeader = () => {
    return (
      <h3
        className={`text-grey-600 font-sans text-xl font-normal ${
          contentType === CONTENT_TYPE.OTHER.value ? 'pl-[586px]' : ''
        }`}
      >
        Create Content Request
      </h3>
    );
  };

  return (
    <div>
      <Shell>
        {hasError && renderError(error)}
        {renderHeader()}
        {renderContentTypeForm(contentType)}
      </Shell>
    </div>
  );
};

Home.propTypes = {
  initialState: PropTypes.string
};

export default Home;
